import { isMePwaSurface } from './mePwaManifest';
import { isMePwaSubdomainHost } from './pwaInstallHosts';

const ME_PWA_SUBDOMAIN_ORIGIN = 'https://me.americanpolicetraining.com';

/** Map apex `/me` paths to `me.*` host paths (e.g. `/me/training/1` → `/training/1`). */
export function resolveMePathForSubdomainHost(pathname: string): string {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (normalized === '/me') {
    return '/';
  }
  if (normalized.startsWith('/me/')) {
    const remainder = normalized.slice('/me'.length);
    return remainder || '/';
  }
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
}

/** Public install/handoff URL on the dedicated `me.*` PWA origin. */
export function resolveMePwaSubdomainUrl(pathname = '/me', hostname?: string): string {
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const path = resolveMePathForSubdomainHost(pathname);
  if (isMePwaSubdomainHost(host) && typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }
  if (
    host === 'americanpolicetraining.com' ||
    host === 'www.americanpolicetraining.com' ||
    host === ''
  ) {
    return `${ME_PWA_SUBDOMAIN_ORIGIN}${path}`;
  }
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }
  return `${ME_PWA_SUBDOMAIN_ORIGIN}${path}`;
}

/** In-app home route for the Me PWA on the current host. */
export function resolveMePwaHomePath(pathname?: string, hostname?: string): string {
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/me');
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  if (isMePwaSurface(path, host)) {
    const primaryLabel = host.split('.')[0]?.toLowerCase() ?? '';
    if (primaryLabel === 'me') {
      return '/';
    }
  }
  return '/me';
}

/** In-app logout route for the Me PWA on the current host. */
export function resolveMePwaLogoutPath(pathname?: string, hostname?: string): string {
  return isMePwaSubdomainHost(
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : ''),
  )
    ? '/logout'
    : '/me/logout';
}

/**
 * True when the current document is inside the Me PWA shell (apex `/me` or `me.*` host).
 * Prefer this over pathname-only checks so subdomain installs keep GPS and nav containment.
 */
export function isMePwaShellContext(pathname?: string, hostname?: string): boolean {
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/me');
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  return isMePwaSurface(path, host);
}

/** Training session detail path for push deep links on the current host. */
export function resolveMePwaTrainingPath(
  sessionId: number | string,
  hostname?: string,
): string {
  const id = encodeURIComponent(String(sessionId));
  return isMePwaSubdomainHost(
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : ''),
  )
    ? `/training/${id}`
    : `/me/training/${id}`;
}

/** Event detail path for the Me PWA on the current host. */
export function resolveMePwaEventPath(
  eventSlug: string,
  pathname?: string,
  hostname?: string,
): string {
  const slug = eventSlug.replace(/^\/+/, '');
  return isMePwaSubdomainHost(
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : ''),
  )
    ? `/${slug}`
    : `/me/${slug}`;
}

/** True when a same-origin path stays inside the Me PWA shell. */
export function isMePwaInternalPath(path: string, pathname?: string, hostname?: string): boolean {
  const normalized = path.replace(/\/+$/, '') || '/';
  const currentPath = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/me');
  const host = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');

  if (normalized === '/me' || normalized.startsWith('/me/')) {
    return true;
  }
  if (normalized === '/logout-confirmation') {
    return true;
  }
  if (!isMePwaSurface(currentPath, host)) {
    return false;
  }
  if (!isMePwaSubdomainHost(host)) {
    return false;
  }
  // On me.* the whole origin is the Me shell (hub, training, logout, event slugs).
  const mainSiteEscapes = new Set([
    '/dashboard',
    '/messages',
    '/notifications',
    '/events',
    '/agencies',
    '/blog',
    '/courses',
    '/tac-shop',
  ]);
  if (mainSiteEscapes.has(normalized) || [...mainSiteEscapes].some((p) => normalized.startsWith(`${p}/`))) {
    return false;
  }
  return true;
}
